import { catalog } from '../core/catalog.js';
import { epg, programProgress } from '../core/epg.js';
import { PlayerCore } from '../core/player.js';
import { nativePreviewCore } from '../core/native-player.js';
import { t } from '../core/i18n.js';
import { device } from '../core/device.js';
import { escapeHtml, formatClock, cleanName, qualityOf } from '../core/utils.js';
import { previewMuted, setPreviewMuted, onPreviewMute } from '../core/preview-sound.js';
import { img, enhance, programMeta } from './render.js';
import { splitProgramDesc } from '../core/epg-desc.js';
import { previewChannel } from '../core/preview-variant.js';
import { warmup } from '../core/warmup.js';

/* Vista «En pantalla» de TV en directo (como en las plataformas de las operadoras): el canal elegido se ve en grande y abajo, sobre
   el vídeo, una hilera de canales. Al recorrer la hilera (flechas del mando, o tocando un canal) el vídeo cambia a ese canal; pulsar
   OK / tocar de nuevo el canal elegido lo abre a pantalla completa. El sonido sigue el estado general de las vistas previas. */

const SWITCH_MS = 600;       // quieto sobre un canal este rato y el vídeo salta a él (pasar deprisa por la hilera no abre nada)
const GIVE_UP_MS = 25000;    // si no ha empezado a sonar, se avisa
const CHUNK = 80;            // canales por tanda en la hilera

const tileHtml = (ch) => {
    const nn = epg.nowNext(ch.epgId);
    const q = qualityOf(ch.name);
    return `<button type="button" class="lst-tile" data-stage-ch="${escapeHtml(String(ch.id))}" aria-label="${escapeHtml(cleanName(ch.name))}">
        <span class="lst-logo">${img(ch.logo, '')}<span class="mi lst-fallback">live_tv</span>${q ? `<span class="lst-q">${q}</span>` : ''}</span>
        <span class="lst-name">${escapeHtml(cleanName(ch.name))}</span>
        <span class="lst-now">${nn.now ? escapeHtml(nn.now.title) : ''}</span>
        ${nn.now ? `<i class="lst-bar"><i style="width:${programProgress(nn.now)}%"></i></i>` : ''}
    </button>`;
};

/** Crea la vista dentro de `host` (ocupa toda la pantalla). `onOpen(id)` abre un canal a pantalla completa; `onExit()` deja la vista. */
export function createStage(host, { onOpen, onExit }) {
    let items = [];
    let catOf = null; // canal -> nombre de su categoría, si la hilera va agrupada por categorías
    let shown = 0;
    let sel = null;
    let selTimer = null;
    let giveUp = null;
    let core = null;
    let video = null;
    let alive = true;
    let paused = false;
    const offs = [];

    host.classList.add('lstage-host');
    document.documentElement.classList.add('stage-open'); // la barra de navegación se esconde mientras esta vista está a la vista
    host.innerHTML = `<div class="lstage" data-lstage>
        <div class="lstage-video" data-lst-video></div>
        <div class="lstage-shade"></div>
        <div class="spinner lstage-wait" data-lst-wait hidden></div>
        <button type="button" class="lstage-exit" data-stage-exit aria-label="${escapeHtml(t('live.stageExit'))}"><span class="mi">grid_view</span><span>${escapeHtml(t('live.stageExit'))}</span></button>
        <div class="lstage-tools">
            <button type="button" class="lstage-ibtn" data-stage-open aria-label="${escapeHtml(t('live.stageOpen'))}" title="${escapeHtml(t('live.stageOpen'))}"><span class="mi">open_in_full</span></button>
            <button type="button" class="lstage-sound" data-lst-sound hidden aria-label="${escapeHtml(t('preview.sound'))}"><span class="mi">volume_off</span></button>
        </div>
        <div class="lstage-info" data-lst-info></div>
        <div class="lstage-strip" data-lst-strip data-nav-row></div>
    </div>`;
    const stageEl = host.querySelector('[data-lstage]');
    const videoBox = host.querySelector('[data-lst-video]');
    const wait = host.querySelector('[data-lst-wait]');
    const soundBtn = host.querySelector('[data-lst-sound]');
    const infoEl = host.querySelector('[data-lst-info]');
    const strip = host.querySelector('[data-lst-strip]');

    const syncStripHeight = () => { stageEl.style.setProperty('--strip-h', strip.offsetHeight + 'px'); }; // el texto y los botones se apoyan justo sobre la hilera, sea cual sea su alto
    if (typeof ResizeObserver !== 'undefined') { const ro = new ResizeObserver(syncStripHeight); ro.observe(strip); offs.push(() => ro.disconnect()); }
    // «Atrás» (mando, Esc, tecla del aparato) deja la vista antes de salir de la página.
    const stageBack = () => { if (paused || !alive) return false; onExit(); return true; };
    if (window.IPTV) window.IPTV.stageBack = stageBack;

    // Con mando, al entrar en la vista el foco va a la hilera (si no, las flechas no hacían nada hasta tocar un canal).
    const focusTile = (tile) => {
        const IP = window.IPTV;
        const tv = (IP && IP.device && IP.device.isTV) || document.documentElement.classList.contains('mode-tv');
        if (!tile || !tv || !IP || !IP.tvnav) return;
        setTimeout(() => { if (alive && !paused && tile.isConnected) IP.tvnav.focus(tile, { scroll: false }); }, 80);
    };
    const byId = (id) => items.find((c) => String(c.id) === String(id)) || null;
    const tileOf = (id) => strip.querySelector(`[data-stage-ch="${window.CSS && CSS.escape ? CSS.escape(String(id)) : String(id)}"]`);

    // Agrupada por categorías (como las filas de la vista anterior): una etiqueta con el nombre de la categoría antes de sus canales.
    const tilesHtml = (list, prev) => {
        if (!catOf) return list.map(tileHtml).join('');
        let last = prev ? catOf.get(prev) : null;
        return list.map((ch) => {
            const c = catOf.get(ch);
            const head = c !== last ? `<span class="lst-cat" aria-hidden="true">${escapeHtml(c || '')}</span>` : '';
            last = c;
            return head + tileHtml(ch);
        }).join('');
    };
    const paintStrip = () => {
        const upto = Math.max(shown, Math.min(CHUNK, items.length));
        shown = Math.min(upto, items.length);
        strip.innerHTML = tilesHtml(items.slice(0, shown), null);
        enhance(strip);
        syncStripHeight();
        if (sel) markSelected();
    };
    const appendMore = () => {
        if (shown >= items.length) return;
        const next = items.slice(shown, shown + CHUNK);
        shown += next.length;
        strip.insertAdjacentHTML('beforeend', tilesHtml(next, items[shown - next.length - 1]));
        enhance(strip);
        if (sel) markSelected();
    };
    const onStripScroll = () => { if (strip.scrollLeft + strip.clientWidth > strip.scrollWidth - 700) appendMore(); };
    strip.addEventListener('scroll', onStripScroll, { passive: true });

    const markSelected = () => {
        strip.querySelectorAll('.lst-tile.is-selected').forEach((x) => x.classList.remove('is-selected'));
        const tile = sel && tileOf(sel.id);
        if (tile) tile.classList.add('is-selected');
        return tile;
    };

    const renderInfo = (note = '') => {
        if (!sel) { infoEl.innerHTML = ''; return; }
        const nn = epg.nowNext(sel.epgId);
        const now = nn.now;
        const prog = now ? splitProgramDesc(now.desc) : null;
        infoEl.innerHTML = `<div class="lst-kicker">${escapeHtml(t('live.nowOn'))}</div>
            <div class="lst-title">${escapeHtml(now ? now.title : cleanName(sel.name))}</div>
            <div class="lst-sub">${escapeHtml(cleanName(sel.name))}${now ? ' · ' + escapeHtml(formatClock(now.start)) + ' - ' + escapeHtml(formatClock(now.stop)) : ''}</div>
            ${now ? `<div class="lst-progress"><i style="width:${programProgress(now)}%"></i></div>` : ''}
            ${prog ? programMeta(prog) : ''}
            ${prog && prog.plot ? `<p class="lst-desc">${escapeHtml(prog.plot)}</p>` : ''}
            ${note ? `<p class="lst-note"><span class="mi">error_outline</span>${escapeHtml(note)}</p>` : ''}`;
    };

    const vmuted = () => (core && core.video ? !!core.video.muted : previewMuted());
    const syncSound = () => {
        if (!video) return;
        soundBtn.querySelector('.mi').textContent = vmuted() ? 'volume_off' : 'volume_up';
    };

    const stopVideo = () => {
        clearTimeout(giveUp);
        if (core) { try { core.destroy(); } catch (e) { /* ya suelto */ } }
        core = null;
        if (video && video.parentNode) video.parentNode.removeChild(video);
        video = null;
        wait.hidden = true;
        soundBtn.hidden = true;
    };

    const play = (ch) => {
        stopVideo();
        if (!alive || paused || !ch) return;
        let url = '';
        try { url = catalog.liveUrl(previewChannel(ch, { big: true })); } catch (e) { url = ''; }
        if (!url) { renderInfo(t('live.stageError')); return; }
        // En la app de Android el canal lo pinta el reproductor nativo (ExoPlayer, el mismo que a pantalla completa: arranca al instante
        // y decodifica HEVC) detrás de la página, que se vuelve transparente en esta vista; si no, el reproductor del navegador.
        const ncore = nativePreviewCore();
        video = document.createElement(ncore ? 'div' : 'video');
        video.className = 'lstage-v';
        if (!ncore) {
            video.muted = previewMuted();
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.poster = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
        }
        videoBox.appendChild(video);
        wait.hidden = false;
        const mine = video;
        core = ncore || new PlayerCore(video);
        const c = core;
        const dead = () => core !== c;
        c.on('playing', () => {
            if (dead()) return;
            clearTimeout(giveUp);
            wait.hidden = true;
            mine.classList.add('is-playing');
            soundBtn.hidden = false;
            syncSound();
            renderInfo();
        });
        c.on('autoplayblocked', () => { if (dead()) return; c.video.muted = true; syncSound(); if (!ncore) mine.play().catch(() => {}); }); // sin permiso para sonar: en silencio
        const fail = () => { if (dead()) return; wait.hidden = true; renderInfo(t('live.stageError')); };
        c.on('error', fail);
        giveUp = setTimeout(fail, GIVE_UP_MS);
        c.load({ url, live: true, muted: previewMuted(), autoplay: true, rect: null, layer: 'back', cssClass: 'native-stage' }).catch(fail);
    };

    /** Elige un canal: la tarjeta y la información cambian al momento; el vídeo, tras un instante (o ya, si se toca). */
    const select = (ch, { now = false } = {}) => {
        if (!ch) return;
        const same = sel && String(sel.id) === String(ch.id);
        sel = ch;
        markSelected();
        renderInfo();
        clearTimeout(selTimer);
        if (same && video) return;
        // Como al enfocar un canal en cualquier lista: se le pide ya la lista al proveedor para que, al saltar el vídeo, el canal esté despierto.
        warmup.request('live', previewChannel(ch, { big: true }).id);
        selTimer = setTimeout(() => play(ch), now ? 0 : SWITCH_MS);
    };

    // Con el mando, el foco recorre la hilera y el vídeo lo sigue; con el ratón o el dedo, tocar un canal lo elige y tocarlo de nuevo lo abre.
    strip.addEventListener('nav:focus', (e) => {
        const tile = e.target.closest && e.target.closest('[data-stage-ch]');
        if (tile && !(sel && String(sel.id) === tile.getAttribute('data-stage-ch'))) select(byId(tile.getAttribute('data-stage-ch')));
    });
    host.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-stage-ch]');
        if (tile) {
            const ch = byId(tile.getAttribute('data-stage-ch'));
            if (!ch) return;
            if (sel && String(sel.id) === String(ch.id) && video) onOpen(ch.id);
            else select(ch, { now: true });
            return;
        }
        if (e.target.closest('[data-stage-exit]')) { onExit(); return; }
        if (e.target.closest('[data-stage-open]') || e.target.closest('.lstage-video')) { if (sel) onOpen(sel.id); return; }
        const snd = e.target.closest('[data-lst-sound]');
        if (snd && video) setPreviewMuted(!vmuted());
    });
    offs.push(onPreviewMute((m) => { if (core && core.video) { core.video.muted = m; syncSound(); } }));

    // Con el reproductor a pantalla completa abierto, la vista se calla (si no, serían dos conexiones al proveedor).
    const player = window.IPTV && window.IPTV.player;
    if (player) {
        offs.push(player.on('open', () => { paused = true; clearTimeout(selTimer); stopVideo(); }));
        offs.push(player.on('close', () => {
            paused = false;
            // Se vuelve al canal que se estaba viendo (el último al que se cambió), con el foco en él.
            const cur = player.mode === 'live' ? byId(player.item && player.item.id) : null;
            if (cur) { sel = cur; markSelected(); renderInfo(); }
            setTimeout(() => {
                if (!alive) return;
                const tile = sel && tileOf(sel.id);
                if (tile) { tile.scrollIntoView({ block: 'nearest', inline: 'center' }); if (device.isTV && window.IPTV.tvnav) window.IPTV.tvnav.focus(tile, { scroll: false }); }
                play(sel);
            }, 150);
        }));
    }

    return {
        /** Pone los canales de la hilera. Si el canal elegido sigue ahí, el vídeo no se toca; si no, se elige el que se vio último (o el primero). */
        setItems(list, groups) {
            // Con `groups` (categorías con sus canales, en su orden) la hilera sigue ese orden y lleva el nombre de cada categoría.
            if (groups && groups.length) {
                items = [];
                catOf = new Map();
                groups.forEach((g) => g.items.forEach((ch) => { items.push(ch); catOf.set(ch, g.name); }));
            } else { items = list; catOf = null; }
            shown = 0;
            const keep = sel && byId(sel.id);
            paintStrip();
            if (keep) { sel = keep; markSelected(); renderInfo(); return; }
            const recent = (catalog.recent || []).map(String);
            const first = recent.map((id) => byId(id)).find(Boolean) || items[0];
            select(first, { now: true });
            const tile = sel && tileOf(sel.id);
            if (tile) { tile.scrollIntoView({ block: 'nearest', inline: 'center' }); focusTile(tile); }
        },
        /** Pone al día la información (llega la guía de programas) sin tocar el vídeo. */
        refreshInfo() { if (sel && !(infoEl.querySelector('.lst-note'))) renderInfo(); },
        /** Se esconde la página (otra pestaña): el vídeo se para; al volver, sigue. */
        pause() { paused = true; stageEl.hidden = true; document.documentElement.classList.remove('stage-open'); clearTimeout(selTimer); stopVideo(); },
        resume() { if (!paused) return; paused = false; stageEl.hidden = false; document.documentElement.classList.add('stage-open'); play(sel); },
        destroy() {
            alive = false;
            clearTimeout(selTimer);
            stopVideo();
            if (window.IPTV && window.IPTV.stageBack === stageBack) window.IPTV.stageBack = null;
            offs.splice(0).forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
            document.documentElement.classList.remove('stage-open');
            host.classList.remove('lstage-host');
            host.innerHTML = '';
        }
    };
}
